/**
 * The clubs a learner belongs to, as the report card needs them.
 *
 * A club is its own thing, not a subject and not a section. It was briefly
 * modelled as a Subject assigned to a section, which could not work: one Grade
 * 6 section has children in Art Club and children in Sports Club, so anything
 * read off the section is wrong for half the room. And a club is not a section
 * either, because Grades 4 to 6 share one - a Section carries a single grade
 * level, and a club modelled as one would hand every member a second report
 * card.
 *
 * So membership is a ClubStudent row keyed on the learner. The card reads the
 * roster directly, which means a club prints from the day the registrar puts
 * the learner in it, with its marks still blank, rather than only once someone
 * has graded it.
 */

/**
 * The row reads "Club", not the club's name.
 *
 * The card is a form: it carries a Conduct line whether or not conduct has been
 * entered, and Club reads the same way. Naming the club would make the line
 * move about from learner to learner - Art Club on one card, Dance Club on the
 * next, nothing on a third - where a form wants one line in one place.
 */
const LABEL = 'Club';

/**
 * @param {Array}  clubStudents  ClubStudent rows for this learner, club included
 * @param {object} schoolYear    the card's school year, to scope the roster
 */
export function clubRows(clubStudents, schoolYear) {
  const schoolYearId = schoolYear && schoolYear.schoolYearId;

  const memberships = (clubStudents || [])
    .filter(function (membership) {
      if (!membership || !membership.club) return false;
      // Last year's roster must not follow a learner into this year's card.
      // With no school year to compare against, nothing is matched rather than
      // every club the learner has ever joined.
      if (!schoolYearId) return false;
      return membership.club.schoolYearId === schoolYearId;
    })
    .sort(function (a, b) {
      return String(a.club.name).localeCompare(String(b.club.name));
    });

  // The line prints either way. A learner in no club, or in one nobody has
  // marked yet, gets an empty Club row rather than no row at all - two cards
  // from the same section should not have a different number of lines.
  if (memberships.length === 0) return [{ membership: null, label: LABEL }];

  // A learner in two clubs gets two lines rather than one club silently
  // winning. Both still read "Club".
  return memberships.map(function (membership) {
    return { membership: membership, label: LABEL };
  });
}

/**
 * This club's mark for one period.
 *
 * Always the numeric column, whatever card this is. A club mark is a number
 * for every member - the club spans grade levels, and how a learner's academic
 * subjects are graded has nothing to do with it. Reading it through the card's
 * own scheme sent a Kinder card looking in the letter column, where no club
 * mark has ever been written.
 */
export function clubMark(membership, scheme, period) {
  if (!membership || !period) return "";

  return Number(membership[period.dbField]) || "";
}
